/**
 * Toolbar — canonical flat filter/action row for sysadmin pages.
 *
 * Per SYSADMIN-PLATFORM-SPEC §11.4:
 *   - Flat row, NO Card wrapper
 *   - Sticky-on-scroll (top-0 with bg + backdrop-blur)
 *   - gap-2 py-3 border-b
 *   - Search input flex-1
 *   - Right side: column toggles + density toggle + refresh
 *
 * The component is layout-only — callers pass their own `Input`,
 * `Select`, `Button` elements as children. The motivation is to
 * eliminate the inline `<div className="flex items-center gap-2
 * sticky top-0 …">` boilerplate that grew up in `audit.stream.tsx`,
 * `tenants/index.tsx`, and the catalog index pages.
 *
 * @example
 * <Toolbar>
 *   <Input placeholder="Search…" className="flex-1 max-w-sm h-9" />
 *   <Select>…</Select>
 *   <Toolbar.Right>
 *     <Button variant="outline" size="sm">Refresh</Button>
 *   </Toolbar.Right>
 * </Toolbar>
 */

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface ToolbarProps {
  children: ReactNode;
  /** Make the toolbar stick to the top of its scroll container. Default true. */
  sticky?: boolean;
  className?: string;
}

function ToolbarRoot({ children, sticky = true, className }: ToolbarProps) {
  return (
    <div
      className={cn(
        'flex items-center gap-2 py-3 border-b border-border/60',
        sticky && 'sticky top-0 z-10 bg-background/95 backdrop-blur-sm',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Right-aligned group inside a Toolbar — pushes its children to the
 * far right via `ml-auto`. Use for refresh / column-toggle / density
 * controls per §11.4.
 */
function ToolbarRight({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn('ml-auto flex items-center gap-2', className)}>
      {children}
    </div>
  );
}

/**
 * A short visual separator (`|`) for splitting toolbar groups. Use
 * sparingly — most groupings are handled by `gap-2`.
 */
function ToolbarDivider({ className }: { className?: string }) {
  return (
    <div className={cn('h-5 w-px bg-border', className)} aria-hidden />
  );
}

export const Toolbar = Object.assign(ToolbarRoot, {
  Right: ToolbarRight,
  Divider: ToolbarDivider,
});
